import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Upload, Loader2, Check, ArrowLeft, Sparkles, X, Maximize2, ImageIcon } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';

import { categoriesService, productsService, aiService } from '@/api/services';
import { optimizeImage, createThumbnail } from '@/utils/image';
import { getCurrencySymbol } from '@/utils/currency';
import { PexelsImagePicker } from '@/components/PexelsImagePicker';
import { env } from '@/lib/env';
import type { Product, CreateProduct, ProductImage, Category, ProductAttribute } from '@/types';

// Form data interface
interface ProductFormData {
    name: string;
    title: string;
    description: string;
    categoryId: string;
    price: string;
    cost: string;
    sku: string;
    status: 'draft' | 'published' | 'archived';
    thumbnailUrl: string;
    attributes: ProductAttribute[];
    images: ProductImage[];
}

interface UploadingImage {
    id: string;
    preview: string;
    progress: number;
    status: 'optimizing' | 'uploading' | 'completed' | 'error';
}

export function ProductFormPage() {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const isEditMode = !!id;

    const [formData, setFormData] = useState<ProductFormData>({
        name: '',
        title: '',
        description: '',
        categoryId: '',
        price: '',
        cost: '',
        sku: '',
        status: 'draft',
        thumbnailUrl: '',
        attributes: [],
        images: []
    });

    const [categories, setCategories] = useState<Category[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);

    // Image State
    const [activeProduct, setActiveProduct] = useState<Product | null>(null); // Draft product for uploads
    const [uploadingFiles, setUploadingFiles] = useState<UploadingImage[]>([]);
    const [imagePreviews, setImagePreviews] = useState<string[]>([]);
    const [isSettingThumbnail, setIsSettingThumbnail] = useState<string | null>(null);
    const [thumbnailSourceUrl, setThumbnailSourceUrl] = useState<string | null>(null);

    // AI State
    const [isGeneratingText, setIsGeneratingText] = useState(false);
    const [isGeneratingImage, setIsGeneratingImage] = useState(false);
    const [imagePrompt, setImagePrompt] = useState('');
    const [expandedImageUrl, setExpandedImageUrl] = useState<string | null>(null);
    const [isPexelsOpen, setIsPexelsOpen] = useState(false);

    // Fetch Categories & Product Data
    const loadData = useCallback(async () => {
        setIsLoading(true);
        try {
            // 1. Fetch Categories
            const catData = await categoriesService.getCategories();
            setCategories(catData?.categories || []);

            // 2. Fetch Product if Edit Mode
            if (isEditMode && id) {
                const product = await productsService.getProduct(id);
                if (product) {
                    setFormData({
                        name: product.name,
                        title: product.title,
                        description: product.description || '',
                        categoryId: product.categoryId,
                        price: String(product.price),
                        cost: String(product.cost || ''),
                        sku: product.sku || '',
                        status: product.status as any,
                        thumbnailUrl: product.thumbnailUrl || '',
                        attributes: product.attributes || [],
                        images: product.images
                    });

                    if (product.thumbnailUrl) {
                        setThumbnailSourceUrl(product.thumbnailUrl);
                    } else if (product.images.length > 0) {
                        setThumbnailSourceUrl(product.images[0].url);
                    }
                    setActiveProduct(product);
                    setImagePreviews(product.images.map(img => img.url));
                }
            }
        } catch (error) {
            console.error('Failed to load data:', error);
            toast.error('Failed to load data');
        } finally {
            setIsLoading(false);
        }
    }, [isEditMode, id]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    // Handlers
    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleStatusChange = (value: string) => {
        setFormData(prev => ({ ...prev, status: value as any }));
    };

    const handleCategoryChange = (val: string) => {
        setFormData(prev => ({ ...prev, categoryId: val }));
    };

    const handleAttributeToggle = (attr: string) => {
        setFormData(prev => {
            const current = prev.attributes || [];
            const updated = current.includes(attr)
                ? current.filter(a => a !== attr)
                : [...current, attr];
            return { ...prev, attributes: updated };
        });
    };

    // AI Description Generation
    const handleGenerateDescription = async () => {
        if (!formData.name || !formData.categoryId) {
            toast.error('Please enter name and category first');
            return;
        }
        const categoryName = categories.find(c => c.id === formData.categoryId)?.name || 'Product';

        setIsGeneratingText(true);
        try {
            const result = await aiService.generateText(formData.name, categoryName);
            setFormData(prev => ({ ...prev, description: result }));
            toast.success('Description generated');
        } catch {
            toast.error('Failed to generate description');
        } finally {
            setIsGeneratingText(false);
        }
    };

    // Image Upload Logic
    const handleImageUpload = async (eventOrFiles: React.ChangeEvent<HTMLInputElement> | File[]) => {
        let files: File[] = [];
        if (Array.isArray(eventOrFiles)) {
            files = eventOrFiles;
        } else if (eventOrFiles instanceof Object && 'target' in eventOrFiles && eventOrFiles.target.files) {
            files = Array.from(eventOrFiles.target.files);
        }

        if (files.length === 0) return;

        // Ensure draft exists
        let currentProduct = activeProduct;
        if (!currentProduct) {
            if (!formData.name || !formData.categoryId) {
                toast.error('Name and Category required to start uploading');
                return;
            }
            try {
                const createData: CreateProduct = {
                    name: formData.name,
                    title: formData.title || formData.name,
                    categoryId: formData.categoryId,
                    status: 'draft',
                };
                currentProduct = await productsService.createProduct(createData);
                setActiveProduct(currentProduct);
                toast.success('Draft created for uploads');
            } catch {
                toast.error('Failed to create draft');
                return;
            }
        }

        // Local Previews
        const newUploads: UploadingImage[] = files.map(file => ({
            id: Math.random().toString(36).substring(7),
            preview: URL.createObjectURL(file),
            progress: 0,
            status: 'optimizing'
        }));
        setUploadingFiles(prev => [...prev, ...newUploads]);

        // Upload Loop
        for (let i = 0; i < files.length; i++) {
            const file = files[i];
            const tracker = newUploads[i];

            try {
                // Optimize
                setUploadingFiles(prev => prev.map(u => u.id === tracker.id ? { ...u, status: 'optimizing', progress: 20 } : u));
                const optimized = await optimizeImage(file, 1080);

                // Upload
                setUploadingFiles(prev => prev.map(u => u.id === tracker.id ? { ...u, status: 'uploading', progress: 40 } : u));

                const uploaded = await productsService.uploadProductImages(currentProduct.productCode, [optimized]);
                if (uploaded && uploaded.length > 0) {
                    const newPreviews = uploaded.map(img => img.url);
                    setImagePreviews(prev => [...prev, ...newPreviews]);

                    // Keep track of new images in formData
                    setFormData(prev => ({
                        ...prev,
                        images: [
                            ...prev.images,
                            ...uploaded.map(img => ({
                                url: img.url,
                                cid: img.cid,
                                filename: img.filename,
                                isThumbnail: false
                            }))
                        ]
                    }));

                    // Update existing product with new images
                    const updated = await productsService.updateProduct(currentProduct.productCode, {
                        ...formData,
                        images: [
                            ...formData.images,
                            ...uploaded.map(img => ({
                                url: img.url,
                                cid: img.cid,
                                filename: img.filename,
                                isThumbnail: false
                            }))
                        ]
                    } as any);
                    setActiveProduct(updated);
                }

                setUploadingFiles(prev => prev.map(u => u.id === tracker.id ? { ...u, status: 'completed', progress: 100 } : u));
            } catch (e) {
                console.error(e);
                setUploadingFiles(prev => prev.map(u => u.id === tracker.id ? { ...u, status: 'error' } : u));
            }

            // Cleanup
            setTimeout(() => {
                setUploadingFiles(prev => prev.filter(u => u.id !== tracker.id));
                URL.revokeObjectURL(tracker.preview);
            }, 1000);
        }
    };

    const handleSetThumbnail = async (url: string) => {
        if (!activeProduct) return;
        setIsSettingThumbnail(url);
        try {
            const response = await fetch(url);
            const blob = await response.blob();
            const file = new File([blob], 'thumbnail.jpg', { type: blob.type });
            const thumbFile = await createThumbnail(file, 256);
            const uploaded = await productsService.uploadProductImages(activeProduct.productCode, [thumbFile]);

            if (uploaded && uploaded.length > 0) {
                const thumbUrl = uploaded[0].url;
                const updatedImages = formData.images.map(img => ({
                    ...img,
                    isThumbnail: img.url === url
                }));
                const updatedProduct = await productsService.updateProduct(activeProduct.productCode, {
                    ...formData,
                    thumbnailUrl: thumbUrl,
                    images: updatedImages
                } as any);
                setActiveProduct(updatedProduct);
                setFormData(prev => ({
                    ...prev,
                    thumbnailUrl: thumbUrl,
                    images: updatedImages
                }));
                setThumbnailSourceUrl(url);
                toast.success('Thumbnail updated');
            }
        } catch {
            toast.error('Failed to set thumbnail');
        } finally {
            setIsSettingThumbnail(null);
        }
    };

    const handleGenerateImage = async () => {
        if (!imagePrompt) return toast.error('Enter prompt');
        setIsGeneratingImage(true);
        try {
            const blob = await aiService.generateImage(imagePrompt);
            const file = new File([blob], `ai-${Date.now()}.png`, { type: 'image/png' });
            await handleImageUpload([file]);
            toast.success('AI Image Generated');
            setImagePrompt('');
        } catch {
            toast.error('Failed to generate image');
        } finally {
            setIsGeneratingImage(false);
        }
    };

    const removeImage = async (urlToRemove: string) => {
        setImagePreviews(prev => prev.filter(url => url !== urlToRemove));
        const isThumbnail = formData.thumbnailUrl === urlToRemove;
        const newImages = formData.images.filter(img => img.url !== urlToRemove);
        const newThumbnailUrl = isThumbnail ? '' : formData.thumbnailUrl;

        setFormData(prev => ({
            ...prev,
            images: newImages,
            thumbnailUrl: newThumbnailUrl
        }));

        if (thumbnailSourceUrl === urlToRemove) {
            setThumbnailSourceUrl(null);
        }

        if (activeProduct) {
            try {
                const updated = await productsService.updateProduct(activeProduct.productCode, {
                    ...formData,
                    images: newImages,
                    thumbnailUrl: newThumbnailUrl
                } as any);
                setActiveProduct(updated);
                toast.success('Image removed');
            } catch (e) {
                console.error('Failed to remove image from product:', e);
                toast.error('Failed to remove image');
            }
        }
    };


    // Main Form Submit
    const saveProduct = async () => {
        if (!formData.name || !formData.categoryId) {
            toast.error('Missing required fields');
            return;
        }
        setIsSaving(true);
        try {
            const dataToSave = {
                ...formData,
                price: parseFloat(formData.price) || 0,
                cost: parseFloat(formData.cost) || 0,
            };

            if (activeProduct) {
                // If we have an activeProduct (from edit mode or draft created during upload), update it
                await productsService.updateProduct(activeProduct.productCode, dataToSave as any);
                toast.success(isEditMode ? 'Product updated' : 'Product saved');
            } else {
                // Create a new product
                await productsService.createProduct(dataToSave as any);
                toast.success('Product created');
            }
            navigate(-1);
        } catch (e) {
            console.error(e);
            toast.error('Failed to save product');
        } finally {
            setIsSaving(false);
        }
    };

    if (isLoading) {
        return <div className="flex justify-center items-center h-screen"><Loader2 className="animate-spin" /></div>;
    }

    return (
        <div className="min-h-screen bg-muted/40 p-6 md:p-10">
            <div className="flex items-center justify-between mb-8">
                <div className="flex items-center gap-4">
                    <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
                        <ArrowLeft className="w-5 h-5" />
                    </Button>
                    <h1 className="text-2xl font-bold tracking-tight text-gray-900">
                        {isEditMode ? 'Edit Product' : 'Add New Product'}
                    </h1>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">

                {/* LEFT COLUMN */}
                <div className="space-y-6">
                    {/* ESSENTIALS CARD (Merged with Pricing) */}
                    <Card className="shadow-sm border-0">
                        <CardHeader className="border-b border-gray-100 bg-white rounded-t-lg px-4 py-3">
                            <CardTitle className="text-lg font-semibold text-gray-800">Product Details</CardTitle>
                        </CardHeader>
                        <CardContent className="px-4 pb-4 pt-3 space-y-4 bg-white rounded-b-lg">

                            <div className="space-y-2">
                                <Label htmlFor="category" className="text-gray-700">Category <span className="text-red-500">*</span></Label>
                                <Select value={formData.categoryId} onValueChange={handleCategoryChange}>
                                    <SelectTrigger className="bg-white border-gray-200">
                                        <SelectValue placeholder="Select category" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="name" className="text-gray-700">Product Name <span className="text-red-500">*</span></Label>
                                <Input id="name" name="name" value={formData.name} onChange={handleChange} className="bg-white" />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="title" className="text-gray-700">Product Title <span className="text-red-500">*</span></Label>
                                <Input id="title" name="title" value={formData.title} onChange={handleChange} className="bg-white" />
                            </div>

                            <div className="space-y-2 relative">
                                <Label htmlFor="description" className="text-gray-700">Description</Label>
                                <div className="relative">
                                    <Textarea
                                        id="description"
                                        name="description"
                                        value={formData.description}
                                        onChange={handleChange}
                                        rows={5}
                                        className="resize-none pr-24 pt-4"
                                        placeholder="Describe the product..."
                                    />
                                    <Button
                                        type="button"
                                        size="sm"
                                        onClick={handleGenerateDescription}
                                        disabled={isGeneratingText}
                                        className="absolute top-2 right-2 h-7 text-xs bg-gray-800 hover:bg-gray-700 text-white"
                                    >
                                        {isGeneratingText ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />}
                                        AI Generate
                                    </Button>
                                </div>
                            </div>


                            {/* Separator for Pricing Section */}
                            <div className="pt-4 border-t border-gray-100">
                                <h3 className="text-sm font-semibold text-gray-800 mb-4">Pricing & Inventory</h3>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="price">Price ({getCurrencySymbol()})</Label>
                                        <Input type="number" id="price" name="price" value={formData.price} onChange={handleChange} />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="cost">Cost ({getCurrencySymbol()})</Label>
                                        <Input type="number" id="cost" name="cost" value={formData.cost} onChange={handleChange} />
                                    </div>
                                </div>
                                <div className="mt-4 space-y-2">
                                    <Input id="sku" name="sku" value={formData.sku} onChange={handleChange} />
                                </div>
                            </div>

                            {/* Attributes Section */}
                            <div className="pt-4 border-t border-gray-100">
                                <h3 className="text-sm font-semibold text-gray-800 mb-4">Product Attributes</h3>
                                <div className="flex flex-wrap gap-2">
                                    {['featured', 'recommended', 'discounted'].map(attr => (
                                        <Badge
                                            key={attr}
                                            variant={formData.attributes.includes(attr) ? 'default' : 'outline'}
                                            className="cursor-pointer capitalize py-1.5 px-3"
                                            onClick={() => handleAttributeToggle(attr)}
                                        >
                                            {attr}
                                        </Badge>
                                    ))}
                                </div>
                                <p className="text-xs text-gray-500 mt-2">
                                    Select attributes to help customers find this product.
                                </p>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* RIGHT COLUMN */}
                <div className="flex flex-col h-full gap-6">

                    {/* VISUAL ASSETS CARD */}
                    <Card className="shadow-sm border-0 flex flex-col flex-grow">
                        <CardHeader className="border-b border-gray-100 bg-white rounded-t-lg px-4 py-3">
                            <CardTitle className="text-lg font-semibold text-gray-800">Visual Assets</CardTitle>
                        </CardHeader>
                        <CardContent className="px-4 pb-4 pt-3 space-y-4 flex-grow bg-white rounded-b-lg">

                            {/* AI Generator & Stock Photos */}
                            <div className="space-y-2">
                                <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">AI Image Generator</Label>
                                <div className="flex gap-2">
                                    <Input
                                        placeholder="Describe image to generate..."
                                        value={imagePrompt}
                                        onChange={(e) => setImagePrompt(e.target.value)}
                                        className="flex-grow"
                                    />
                                    <Button
                                        onClick={handleGenerateImage}
                                        disabled={isGeneratingImage || !imagePrompt}
                                        className="bg-gray-800 hover:bg-gray-900 text-white whitespace-nowrap"
                                    >
                                        {isGeneratingImage ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Generate'}
                                    </Button>
                                </div>
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => {
                                        if (!env.PEXELS_API) {
                                            toast.error('Pexels API key not configured. Set VITE_PEXELS_API_KEY in your environment.');
                                            return;
                                        }
                                        setIsPexelsOpen(true);
                                    }}
                                    className="w-full mt-1"
                                >
                                    <ImageIcon className="w-4 h-4 mr-2" />
                                    Search Stock Photos (Pexels)
                                </Button>
                            </div>

                            {/* Main Preview with Fallback */}
                            <div className="relative w-full aspect-[4/3] bg-gray-100 rounded-lg overflow-hidden border border-gray-200 group">
                                {(thumbnailSourceUrl || imagePreviews[0]) ? (
                                    <>
                                        <img
                                            src={thumbnailSourceUrl || imagePreviews[0]}
                                            alt="Main Preview"
                                            className="w-full h-full object-contain bg-gray-50"
                                        />
                                        <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-black/60 to-transparent flex justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                            <Button
                                                size="sm"
                                                variant="secondary"
                                                className="bg-white/20 hover:bg-white/40 text-white border-0 mr-auto"
                                                onClick={() => setExpandedImageUrl(thumbnailSourceUrl || imagePreviews[0])}
                                            >
                                                <Maximize2 className="w-4 h-4" />
                                            </Button>
                                            {(formData.thumbnailUrl === (thumbnailSourceUrl || imagePreviews[0])) ? (
                                                <Button size="sm" variant="secondary" className="bg-green-500 text-white hover:bg-green-600 cursor-default">
                                                    <Check className="w-4 h-4 mr-1" /> Thumbnail
                                                </Button>
                                            ) : (
                                                <Button size="sm" variant="secondary" disabled={!!isSettingThumbnail} onClick={() => handleSetThumbnail(thumbnailSourceUrl || imagePreviews[0])}>
                                                    {isSettingThumbnail ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                                                    Set as Thumbnail
                                                </Button>
                                            )}
                                            <Button size="sm" variant="destructive" onClick={() => removeImage(thumbnailSourceUrl || imagePreviews[0])}>
                                                Remove
                                            </Button>
                                        </div>
                                    </>
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center text-gray-400">
                                        <Upload className="w-12 h-12 opacity-50" />
                                    </div>
                                )}
                            </div>

                            {/* Thumbnails Grid */}
                            <div className="grid grid-cols-4 gap-4">
                                {imagePreviews.map((url, idx) => (
                                    <div key={idx} className="group/item">
                                        <div
                                            onClick={() => setThumbnailSourceUrl(url)}
                                            className={`aspect-square rounded-lg border-2 overflow-hidden cursor-pointer relative ${thumbnailSourceUrl === url ? 'border-gray-800' : 'border-transparent hover:border-gray-300'}`}
                                        >
                                            <img src={url} alt={`Thumb ${idx}`} className="w-full h-full object-contain bg-gray-50" />

                                            {/* Remove Button (Red X) */}
                                            <div
                                                onClick={(e) => { e.stopPropagation(); removeImage(url); }}
                                                className="absolute top-1 right-1 bg-red-500 rounded-full p-1 shadow-sm opacity-0 group-hover/item:opacity-100 transition-opacity hover:bg-red-600 cursor-pointer z-10"
                                            >
                                                <X className="w-3 h-3 text-white" />
                                            </div>

                                            {/* Thumbnail Indicator Badge Overlay */}
                                            {formData.images.find(img => img.url === url)?.isThumbnail && (
                                                <div className="absolute inset-x-0 bottom-0 bg-green-500/90 py-0.5">
                                                    <p className="text-[10px] font-bold text-white text-center uppercase tracking-tighter">Thumbnail</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}

                                {/* Upload More Box */}
                                <Label className="aspect-square rounded-lg border-2 border-dashed border-gray-300 flex flex-col items-center justify-center cursor-pointer hover:border-gray-500 hover:bg-gray-50 transition-colors text-gray-500">
                                    <Upload className="h-6 w-6 mb-1 text-gray-400" />
                                    <span className="text-[10px] font-medium uppercase">Upload</span>
                                    <input type="file" className="hidden" multiple accept="image/*" onChange={handleImageUpload} />
                                </Label>

                                {/* Uploading Placeholders */}
                                {uploadingFiles.map(file => (
                                    <div key={file.id} className="aspect-square rounded-lg border border-gray-200 p-2 flex items-center justify-center bg-gray-50">
                                        <Progress value={file.progress} className="h-1 w-full" />
                                    </div>
                                ))}
                            </div>

                            <div className="space-y-1">
                                <Label htmlFor="status">Status</Label>
                                <Select value={formData.status} onValueChange={handleStatusChange}>
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="draft">Draft</SelectItem>
                                        <SelectItem value="published">Published</SelectItem>
                                        <SelectItem value="archived">Archived</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>

                        </CardContent>
                    </Card >

                    {/* ACTIONS */}
                    < div className="flex justify-end gap-4 mt-auto pt-4" >
                        <Button variant="outline" onClick={() => navigate(-1)}>
                            Cancel
                        </Button>
                        <Button
                            onClick={saveProduct}
                            disabled={isSaving}
                            className="bg-gray-800 hover:bg-gray-900 text-white shadow-lg shadow-gray-300/50 min-w-[120px]"
                        >
                            {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : (isEditMode ? 'Update Product' : 'Add Product')}
                        </Button>
                    </div >
                </div >

            </div >

            {/* Pexels Stock Photo Picker */}
            <PexelsImagePicker
                open={isPexelsOpen}
                onOpenChange={setIsPexelsOpen}
                onImagesSelected={handleImageUpload}
                productName={formData.name}
            />

            {/* Image Full Screen Modal */}
            {
                expandedImageUrl && (
                    <div
                        className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4 drop-shadow-2xl animate-in fade-in duration-200"
                        onClick={() => setExpandedImageUrl(null)}
                    >
                        <Button
                            variant="ghost"
                            size="icon"
                            className="absolute top-4 right-4 text-white hover:bg-white/20"
                            onClick={() => setExpandedImageUrl(null)}
                        >
                            <X className="w-8 h-8" />
                        </Button>
                        <img
                            src={expandedImageUrl}
                            alt="Expanded View"
                            className="max-w-full max-h-full object-contain rounded-lg shadow-2xl scale-in-95 animate-in duration-200"
                        />
                    </div>
                )
            }
        </div >
    );
}
